import AttachFileOutlinedIcon from "@mui/icons-material/AttachFileOutlined";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import ForwardToInboxOutlinedIcon from "@mui/icons-material/ForwardToInboxOutlined";
import GroupOutlinedIcon from "@mui/icons-material/GroupOutlined";
import SendOutlinedIcon from "@mui/icons-material/SendOutlined";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import {
  Autocomplete,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  FormControlLabel,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from "@mui/material";
import { useMutation, useQuery } from "@tanstack/react-query";
import axios from "axios";
import DOMPurify from "dompurify";
import InputField from "components/Input/InputField";
import UploadFile from "components/Input/UploadFile";
import { differenceInDays } from "date-fns";
import { useEffect, useMemo, useRef, useState } from "react";
import ReactQuill, { Quill } from "react-quill";
import "react-quill/dist/quill.snow.css";
import "quill-emoji/dist/quill-emoji.css";
import { toast } from "react-toastify";
import EmailTemplatesModal from "components/Modals/EmailTemplatesModal";
import { hasPermission } from "utils/permissionUtils";
import { useLocation, useNavigate } from "react-router-dom";
import { Chip, Paper, Stack } from "@mui/material";
import { getOriginalFileName } from "utils/utils";
import { getClientData, getPaginatedClientData } from "utils/clientService";
import ImageResize from "quill-image-resize-module-plus";
import { ImageDrop } from "quill-image-drop-module";
import QuillEmoji from "quill-emoji";
import "./composeEmailQuill.css";
import { FROM_EMAILS, DEFAULT_FROM_EMAIL } from "const/emailSenders";
import { P } from "const/permissions";
import { getStoredMemberships } from "utils/organizationAccess";
import { sendWhatsAppBulk } from "api/whatsapp";
import { WHATSAPP_CATEGORY_OPTIONS, hasValidPhone, htmlToPlainText } from "utils/whatsappMessage";

const TO_SEARCH_PAGE_SIZE = 20;
const TO_SEARCH_DEBOUNCE_MS = 350;

const TO_CATEGORY_OPTIONS = [
  { label: "All Clients with valid email", value: "all" },
  { label: "Clients with Visa Expiry", value: "visaExpiry" },
  { label: "Clients with Birthday Today", value: "birthday" },
  { label: "Clients with Passport Expiry", value: "passportExpiry" },
];

const filterClientsByCategory = (clients, categoryValue, channel = "email") => {
  const list = Array.isArray(clients) ? clients : [];
  const matched = (() => {
    if (categoryValue === "all") {
      return channel === "whatsapp"
        ? list.filter(hasValidPhone)
        : list.filter((client) => client.email?.length > 0);
    }
    if (categoryValue === "visaExpiry") {
      return list.filter((client) => {
        const daysUntilExpiry = differenceInDays(new Date(client.visaExpiry), new Date());
        return client.visaExpiry && daysUntilExpiry < 90 && daysUntilExpiry > 0;
      });
    }
    if (categoryValue === "birthday") {
      const today = new Date();
      return list.filter((client) => {
        const birthday = new Date(client.dob);
        return birthday.getMonth() === today.getMonth() && birthday.getDate() === today.getDate();
      });
    }
    if (categoryValue === "passportExpiry") {
      return list.filter((client) => {
        const daysUntilExpiry = differenceInDays(new Date(client.passportExpiry), new Date());
        return client.passportExpiry && daysUntilExpiry < 90 && daysUntilExpiry > 0;
      });
    }
    return list;
  })();
  if (channel === "whatsapp" && categoryValue !== "all") {
    return matched.filter(hasValidPhone);
  }
  return matched;
};
const escapeHtml = (value = "") =>
  String(value)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");

/** Matches the backend bulk-email HTML wrapper (ORG_NAME, CAPTION_LABEL, CONTENT_HTML, CONTACT_LINE). */
const buildBrandedEmailPreviewHtml = ({ orgName, captionLabel, contentHtml, contactLine }) => {
  const safeOrg = escapeHtml(orgName || "Your Organization");
  const safeCaption = escapeHtml(captionLabel || "CLIENT UPDATE");
  const safeContact = escapeHtml(contactLine || "");
  const bodyContent = contentHtml || "<p>(No message body)</p>";

  return `<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="utf-8" />
  <meta name="viewport" content="width=device-width, initial-scale=1.0" />
  <title>Email preview</title>
</head>
<body style="margin:0; padding:0; font-family:Arial,Helvetica,sans-serif; background-color:#F3F6FA;">
  <table role="presentation" width="100%" cellpadding="0" cellspacing="0" bgcolor="#F3F6FA" style="background:#F3F6FA;">
    <tr>
      <td style="padding:24px 20px;" align="center">
        <table role="presentation" width="600" cellpadding="0" cellspacing="0" style="width:600px; max-width:100%; background:#ffffff; border-radius:12px; box-shadow:0 4px 16px rgba(0,0,0,0.08); overflow:hidden;">
          <tr>
            <td bgcolor="#1d52a8" style="background:#1d52a8; padding:22px 30px; text-align:center; border-bottom:3px solid #E67E22;">
              <p style="margin:0; font-size:20px; font-weight:700; letter-spacing:0.5px; color:#FFFFFF; font-family:Arial,Helvetica,sans-serif;">
                ${safeOrg}
              </p>
              <p style="margin:6px 0 0 0; font-size:11px; font-weight:700; letter-spacing:1.5px; color:rgba(255,255,255,0.85); font-family:Arial,Helvetica,sans-serif;">
                ${safeCaption}
              </p>
            </td>
          </tr>
          <tr>
            <td bgcolor="#ffffff" style="background:#ffffff; padding:28px 32px; color:#334155; font-size:14px; line-height:1.6; font-family:Arial,Helvetica,sans-serif;">
              ${bodyContent}
            </td>
          </tr>
          <tr>
            <td bgcolor="#ffffff" style="background:#ffffff; padding:14px 32px; border-top:1px solid #e2e8f0; color:#64748b; font-size:11px; line-height:1.5; font-family:Arial,Helvetica,sans-serif;">
              Sent by ${safeOrg}.${safeContact ? ` ${safeContact}` : ""}
            </td>
          </tr>
        </table>
      </td>
    </tr>
  </table>
</body>
</html>`;
};

const getActiveOrganizationName = () => {
  try {
    const userDetail = JSON.parse(localStorage.getItem("userDetail") || "{}");
    if (userDetail?.organization?.name) return userDetail.organization.name;
    const activeOrgId = userDetail?.activeOrgId ?? userDetail?.orgId;
    const membership = getStoredMemberships().find((item) => item.orgId === activeOrgId);
    return membership?.orgName || "Your Organization";
  } catch {
    return "Your Organization";
  }
};

const getOrganizationContactLine = (fromEmail) => {
  try {
    const userDetail = JSON.parse(localStorage.getItem("userDetail") || "{}");
    const org = userDetail?.organization || {};
    const parts = [org.email, org.phone || org.number].filter(Boolean);
    if (parts.length > 0) return parts.join(" · ");
    if (fromEmail) return `For enquiries contact ${fromEmail}.`;
    return "";
  } catch {
    return fromEmail ? `For enquiries contact ${fromEmail}.` : "";
  }
};

// Quill module/format registration (run once)
// eslint-disable-next-line no-underscore-dangle
if (!Quill.__KOTHAR_COMPOSE_EMAIL_ENHANCED__) {
  // eslint-disable-next-line no-underscore-dangle
  Quill.__KOTHAR_COMPOSE_EMAIL_ENHANCED__ = true;

  try {
    const Font = Quill.import("formats/font");
    Font.whitelist = [
      "arial",
      "calibri",
      "georgia",
      "helvetica",
      "times-new-roman",
      "trebuchet-ms",
      "verdana",
      "serif",
      "sans-serif",
      "monospace",
    ];
    Quill.register(Font, true);
  } catch {
    // ignore font whitelist failures
  }

  Quill.register("modules/imageResize", ImageResize);
  Quill.register("modules/imageDrop", ImageDrop);

  const QE = QuillEmoji?.QuillEmoji ?? QuillEmoji;
  const EmojiBlot = QE?.EmojiBlot;
  const ShortNameEmoji = QE?.ShortNameEmoji;
  const ToolbarEmoji = QE?.ToolbarEmoji;
  const TextAreaEmoji = QE?.TextAreaEmoji;

  Quill.register(
    {
      ...(EmojiBlot ? { "formats/emoji": EmojiBlot } : {}),
      ...(ToolbarEmoji ? { "modules/emoji-toolbar": ToolbarEmoji } : {}),
      ...(TextAreaEmoji ? { "modules/emoji-textarea": TextAreaEmoji } : {}),
      ...(ShortNameEmoji ? { "modules/emoji-shortname": ShortNameEmoji } : {}),
    },
    true
  );
}

const SectionHeader = ({ icon, title, trailing }) => (
  <div className="px-4 py-3 border-b border-app-border flex items-center justify-between gap-3 bg-app-surface-muted/40">
    <div className="flex items-center gap-2.5">
      <span className="flex items-center justify-center w-7 h-7 rounded-lg bg-primary/10 text-primary">
        {icon}
      </span>
      <Typography variant="subtitle2" className="font-semibold text-app-text">
        {title}
      </Typography>
    </div>
    {trailing}
  </div>
);

const ComposeEmailPage = () => {
  const navigate = useNavigate();
  const { email } = useLocation()?.state || {};
  const canEmail = hasPermission(P.EMAIL.MESSAGE_COMPOSE);
  const canWhatsApp = hasPermission(P.MESSAGING.WHATSAPP_SEND);
  const [channel, setChannel] = useState(canEmail ? "email" : "whatsapp");
  const [recordOptIn, setRecordOptIn] = useState(false);
  const [from, setFrom] = useState(DEFAULT_FROM_EMAIL);
  const [to, setTo] = useState(email ? [email] : []);
  const [toCategory, setToCategory] = useState(null);
  const [cc] = useState([]); // no CC UI on the current drawer implementation
  const [tags, setTags] = useState([]);
  const [template, setTemplate] = useState(null);
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [attachments, setAttachments] = useState([]);
  const [templatesModalOpen, setTemplatesModalOpen] = useState(false);
  const [externalEmailsPaste, setExternalEmailsPaste] = useState("");
  const [festivalName, setFestivalName] = useState("");
  const rawTemplateRef = useRef({ subject: "", body: "" });
  const subjectRef = useRef();
  const quillRef = useRef();

  const [custom, setCustom] = useState(false);
  const [allClients, setAllClients] = useState([]);
  const [categoryLoading, setCategoryLoading] = useState(false);
  const [toOptions, setToOptions] = useState([]);
  const [toInputValue, setToInputValue] = useState("");
  const [debouncedToSearch, setDebouncedToSearch] = useState("");
  const [toOptionsLoading, setToOptionsLoading] = useState(false);
  const [toOpen, setToOpen] = useState(false);
  const toRequestIdRef = useRef(0);

  const emailLike = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

  const quillModules = useMemo(
    () => ({
      toolbar: [
        [{ font: [] }, { size: [] }],
        [{ header: [1, 2, 3, false] }],
        ["bold", "italic", "underline", "strike"],
        [{ color: [] }, { background: [] }],
        [{ script: "sub" }, { script: "super" }],
        [{ list: "ordered" }, { list: "bullet" }, { indent: "-1" }, { indent: "+1" }],
        [{ align: [] }],
        ["blockquote", "code-block"],
        ["link", "image", "video"],
        ["emoji"],
        ["clean"],
      ],
      clipboard: { matchVisual: false },
      imageDrop: true,
      imageResize: {
        parchment: Quill.import("parchment"),
        modules: ["Resize", "DisplaySize", "Toolbar"],
      },
      "emoji-toolbar": true,
      "emoji-textarea": false,
      "emoji-shortname": true,
    }),
    []
  );

  const quillFormats = useMemo(
    () => [
      "header",
      "font",
      "size",
      "bold",
      "italic",
      "underline",
      "strike",
      "color",
      "background",
      "script",
      "list",
      "bullet",
      "indent",
      "align",
      "blockquote",
      "code-block",
      "link",
      "image",
      "video",
      "emoji",
      "clean",
    ],
    []
  );

  const parseExternalEmailsWithInvalid = (text) => {
    if (!text?.trim()) return { valid: [], invalid: [] };
    const raw = text
      .split(/[\r\n\t,;]+/)
      .map((s) => s.trim().toLowerCase())
      .filter(Boolean);
    const valid = [];
    const invalid = [];
    const seenValid = new Set();
    const seenInvalid = new Set();

    raw.forEach((e) => {
      if (emailLike.test(e)) {
        if (!seenValid.has(e)) {
          seenValid.add(e);
          valid.push(e);
        }
      } else if (!seenInvalid.has(e)) {
        seenInvalid.add(e);
        invalid.push(e);
      }
    });

    return { valid, invalid };
  };

  const parseExternalEmails = (text) => {
    const { valid } = parseExternalEmailsWithInvalid(text);
    return valid;
  };

  const getEmailTemplates = async () => {
    const response = await axios.get("/email-templates");
    return response?.data;
  };

  const { data: emailTemplates = [], refetch: refetchEmailTemplates } = useQuery(
    ["emailTemplates"],
    getEmailTemplates,
    {
      enabled: canEmail,
      refetchOnWindowFocus: false,
      retry: false,
    }
  );

  const { mutate: sendEmail, isLoading: emailSending } = useMutation({
    mutationFn: (data) => axios.post("/send-email/bulk", data),
    onSuccess: () => {
      toast.success("Email sent to the clients!");
      setCustom(false);
    },
    onError: () => {
      toast.error("Failed to send email");
      setCustom(false);
    },
  });

  const { mutate: sendWhatsApp, isLoading: whatsappSending } = useMutation({
    mutationFn: (data) => sendWhatsAppBulk(data),
    onSuccess: (response) => {
      const result = response?.data?.data;
      if (result?.batchStatus === "FAILED") {
        const first = result.results?.find((item) => item.errorMessage)?.errorMessage;
        toast.error(first || "WhatsApp could not send the message");
        return;
      }
      if (result?.batchStatus === "PARTIAL") {
        toast.warning(
          `WhatsApp sent to ${result.successCount} of ${result.totalCount}. The rest are in Messages.`
        );
      } else {
        toast.success("WhatsApp sent. Each person got their own chat.");
      }
      setRecordOptIn(false);
    },
    onError: (error) => {
      toast.error(error?.response?.data?.message || "Failed to send WhatsApp");
    },
  });

  const isLoading = emailSending || whatsappSending;
  const isWhatsApp = channel === "whatsapp";
  const categoryOptions = isWhatsApp ? WHATSAPP_CATEGORY_OPTIONS : TO_CATEGORY_OPTIONS;

  useEffect(() => {
    const timer = setTimeout(
      () => setDebouncedToSearch(toInputValue.trim()),
      TO_SEARCH_DEBOUNCE_MS
    );
    return () => clearTimeout(timer);
  }, [toInputValue]);

  // Manual "To" picker: paginated search only (no full client list)
  useEffect(() => {
    if (toCategory || !toOpen) return;

    const requestId = ++toRequestIdRef.current;
    let cancelled = false;

    const load = async () => {
      setToOptionsLoading(true);
      try {
        const page = await getPaginatedClientData({
          pageIndex: 0,
          pageSize: TO_SEARCH_PAGE_SIZE,
          searchText: debouncedToSearch || undefined,
        });
        if (cancelled || requestId !== toRequestIdRef.current) return;
        setToOptions(Array.isArray(page?.content) ? page.content : []);
      } catch {
        if (cancelled || requestId !== toRequestIdRef.current) return;
        setToOptions([]);
      } finally {
        if (!cancelled && requestId === toRequestIdRef.current) {
          setToOptionsLoading(false);
        }
      }
    };

    load();
    return () => {
      cancelled = true;
    };
  }, [debouncedToSearch, toOpen, toCategory]);

  const handleCategoryChange = async (_, val) => {
    setToCategory(val);
    if (!val) {
      setTo([]);
      setAllClients([]);
      return;
    }

    setCategoryLoading(true);
    try {
      // Full list only when a bulk category is selected
      const clients = allClients.length > 0 ? allClients : await getClientData();
      const list = Array.isArray(clients) ? clients : [];
      if (!allClients.length) setAllClients(list);
      setTo(filterClientsByCategory(list, val.value, channel));
    } catch {
      toast.error("Failed to load clients");
      setTo([]);
    } finally {
      setCategoryLoading(false);
    }
  };

  const isFestivalTemplate = useMemo(
    () => emailTemplates?.find((t) => t.id === template)?.templateName === "Festival Greetings",
    [template, emailTemplates]
  );

  const applyTemplate = (tpl) => {
    rawTemplateRef.current = { subject: tpl?.subject || "", body: tpl?.body || "" };
    setFestivalName("");
    setSubject(tpl?.subject || "");
    setBody(tpl?.body || "");
  };

  const handleTemplateChange = (val) => {
    const tpl = emailTemplates?.find((t) => t?.id === val?.value);
    setTemplate(tpl?.id || null);
    applyTemplate(tpl);
  };

  const handleTemplateSelectFromModal = (selectedTemplate) => {
    setTemplate(selectedTemplate.id);
    applyTemplate(selectedTemplate);
    setCustom(false);
  };

  const handleFestivalNameChange = (e) => {
    const name = e.target.value;
    setFestivalName(name);
    setSubject(rawTemplateRef.current.subject.replace(/\{festivalName\}/g, name));
    setBody(rawTemplateRef.current.body.replace(/\{festivalName\}/g, name));
  };

  const switchChannel = (_, next) => {
    if (!next || next === channel) return;
    setChannel(next);
    setTo(email && next === "email" ? [email] : []);
    setToCategory(null);
    setAllClients([]);
    setRecordOptIn(false);
  };

  const handleSendWhatsApp = () => {
    const plain = htmlToPlainText(body);
    const text = [subject?.trim(), plain].filter(Boolean).join("\n\n");
    const media = (attachments?.attachments || []).map((key) => ({
      storageKey: key,
      fileName: getOriginalFileName(key) || "file",
    }));
    if (!text && media.length === 0) {
      toast.error("Enter a message or attach a file");
      return;
    }
    if (!recordOptIn) {
      toast.error("Confirm that these clients have opted in to WhatsApp");
      return;
    }
    const reachable = to.filter(
      (item) => item && typeof item === "object" && item.id && hasValidPhone(item)
    );
    if (reachable.length === 0) {
      toast.error("Select at least one client with a valid mobile number");
      return;
    }
    if (reachable.length < to.length) {
      toast.warn(
        `${to.length - reachable.length} selected ${
          to.length - reachable.length === 1 ? "person was" : "people were"
        } skipped because they have no valid mobile number`
      );
    }
    sendWhatsApp({
      clientIds: reachable.map((item) => item.id),
      text,
      recordOptIn: true,
      subject: subject?.trim() || "",
      tags,
      source: "COMPOSE",
      media,
    });
  };

  const handleSend = (e) => {
    e.preventDefault();
    if (isWhatsApp) {
      handleSendWhatsApp();
      return;
    }
    if (!from || !subject || !body) {
      toast.error("Please fill all required fields");
      return;
    }
    if (isFestivalTemplate && !festivalName.trim()) {
      toast.error("Please enter the festival name before sending");
      return;
    }
    if (body.includes("{offerDetails}")) {
      toast.error("Replace {offerDetails} with actual offer details before sending");
      return;
    }
    if (to.length === 0 && externalEmailsPaste.trim() === "") {
      toast.error("Please select at least one recipient or paste external emails");
      return;
    }

    // The To box is free-typed: a hand-typed name has no client id and would
    // otherwise fail per-recipient as "client-id:null". Skip those entries.
    const clientIds = to
      .map((t) => (t && typeof t === "object" ? t.id : null))
      .filter((id) => id !== null && id !== undefined);
    const ccRecipients = Array.isArray(cc)
      ? cc.map((c) => (typeof c === "string" ? c : c?.email ?? c)).filter(Boolean)
      : [];
    const bccRecipients = [];
    const externalEmails = parseExternalEmails(externalEmailsPaste);

    if (clientIds.length === 0 && externalEmails.length === 0) {
      toast.error("No valid client selected — pick clients from the list or paste external emails");
      return;
    }
    if (clientIds.length < to.length) {
      toast.warn(
        `${to.length - clientIds.length} selected ${
          to.length - clientIds.length === 1 ? "entry was" : "entries were"
        } not linked to a client record and ${
          to.length - clientIds.length === 1 ? "was" : "were"
        } skipped`
      );
    }

    const request = {
      clientIds,
      externalEmails,
      templateId: custom ? 0 : template ?? 0,
      ccRecipients,
      bccRecipients,
      customSubject: custom ? subject : "",
      customBody: custom ? body : "",
      fromEmail: from ?? "",
      tags,
      external: externalEmails.length > 0,
      attachmentKeys: attachments?.attachments?.map((attachment) => attachment),
    };

    sendEmail(request);
  };

  const filteredList = useMemo(() => {
    if (toCategory?.value) {
      return filterClientsByCategory(allClients, toCategory.value, channel);
    }
    if (isWhatsApp) {
      return toOptions.filter(hasValidPhone);
    }
    return toOptions;
  }, [toCategory, allClients, toOptions, channel, isWhatsApp]);

  const externalEmailsParsed = useMemo(
    () => parseExternalEmails(externalEmailsPaste),
    [externalEmailsPaste]
  );

  const toPreviewEmails = useMemo(() => {
    const selectedClientEmails = (to || []).map((t) => t?.email).filter(Boolean);
    const external = externalEmailsParsed || [];
    return [...selectedClientEmails, ...external];
  }, [to, externalEmailsParsed]);

  const whatsappPreviewNumbers = useMemo(
    () => (to || []).map((item) => item?.number).filter(Boolean),
    [to]
  );

  const toPreviewLabel = useMemo(() => {
    const values = isWhatsApp ? whatsappPreviewNumbers : toPreviewEmails;
    if (values.length === 0) return "—";
    const first = values.slice(0, 3).join(", ");
    return values.length > 3 ? `${first} +${values.length - 3} more` : first;
  }, [isWhatsApp, whatsappPreviewNumbers, toPreviewEmails]);

  const whatsappPreviewText = useMemo(() => {
    const plain = htmlToPlainText(body);
    return [subject?.trim(), plain].filter(Boolean).join("\n\n") || "(No message body)";
  }, [subject, body]);

  const sanitizedBody = useMemo(() => {
    const raw = body || "";
    if (!raw.trim()) return "<p>(No message body)</p>";
    return DOMPurify.sanitize(raw);
  }, [body]);

  const selectedTemplateName = useMemo(
    () => emailTemplates?.find((item) => item.id === template)?.templateName || "",
    [emailTemplates, template]
  );

  const emailPreviewHtml = useMemo(() => {
    const orgName = getActiveOrganizationName();
    const captionLabel = (
      (tags.length > 0 ? tags.join(" · ") : "") ||
      selectedTemplateName ||
      subject ||
      "CLIENT UPDATE"
    ).toUpperCase();
    const contactLine = getOrganizationContactLine(from);

    return buildBrandedEmailPreviewHtml({
      orgName,
      captionLabel,
      contentHtml: sanitizedBody,
      contactLine,
    });
  }, [sanitizedBody, selectedTemplateName, tags, from]);

  return (
    <div className="p-4 md:p-6 bg-app-bg min-h-full">
      {/* Sticky header */}
      <div className="sticky top-0 z-10 -mx-4 md:-mx-6 px-4 md:px-6 py-3.5 mb-5 bg-app-bg/95 backdrop-blur border-b border-app-border-subtle flex items-center justify-between gap-3">
        <div className="flex items-center gap-3 min-w-0">
          <div className="hidden sm:flex items-center justify-center w-10 h-10 rounded-xl bg-primary/10 text-primary shrink-0">
            <ForwardToInboxOutlinedIcon />
          </div>
          <div className="min-w-0">
            <Typography variant="h6" className="font-semibold text-app-text leading-tight">
              Compose message
            </Typography>
            <Typography variant="caption" className="text-app-text-muted">
              {isWhatsApp
                ? "WhatsApp goes to each person separately. It does not create a group."
                : "Send a templated or custom email to one or more clients"}
            </Typography>
          </div>
        </div>

        <div className="flex items-center gap-2 shrink-0">
          <Button variant="outlined" onClick={() => navigate("/admin/emails")}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color="primary"
            disabled={isLoading}
            onClick={handleSend}
            startIcon={
              isLoading ? (
                <CircularProgress size={16} color="inherit" />
              ) : (
                <SendOutlinedIcon fontSize="small" />
              )
            }
          >
            {isLoading ? "Sending…" : "Send"}
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-12 gap-5 items-start">
        {/* Inputs */}
        <form className="xl:col-span-5 flex flex-col gap-5" onSubmit={handleSend}>
          {canEmail && canWhatsApp && (
            <ToggleButtonGroup
              exclusive
              size="small"
              color="primary"
              value={channel}
              onChange={switchChannel}
              aria-label="Message channel"
            >
              <ToggleButton value="email">Email</ToggleButton>
              <ToggleButton value="whatsapp">WhatsApp</ToggleButton>
            </ToggleButtonGroup>
          )}

          <Typography variant="caption" color="text.secondary" className="font-medium -mb-2">
            * fields are mandatory.
          </Typography>

          {/* Recipients */}
          <Paper
            elevation={0}
            className="border border-app-border rounded-xl bg-app-surface shadow-sm overflow-hidden"
          >
            <SectionHeader
              icon={<GroupOutlinedIcon fontSize="small" />}
              title="Recipients"
              trailing={
                <Chip
                  size="small"
                  label={`${
                    isWhatsApp ? whatsappPreviewNumbers.length : toPreviewEmails.length
                  } recipient${
                    (isWhatsApp ? whatsappPreviewNumbers.length : toPreviewEmails.length) === 1
                      ? ""
                      : "s"
                  }`}
                  color={
                    (isWhatsApp ? whatsappPreviewNumbers.length : toPreviewEmails.length) > 0
                      ? "primary"
                      : "default"
                  }
                  variant={
                    (isWhatsApp ? whatsappPreviewNumbers.length : toPreviewEmails.length) > 0
                      ? "filled"
                      : "outlined"
                  }
                />
              }
            />
            <div className="p-4 flex flex-col gap-4">
              {!isWhatsApp && (
                <Autocomplete
                  options={FROM_EMAILS}
                  value={from}
                  onChange={(e, val) => setFrom(val)}
                  renderInput={(params) => <TextField {...params} label="From" />}
                  getOptionLabel={(option) => option}
                  isOptionEqualToValue={(option, value) => option === value}
                  size="small"
                />
              )}

              <Autocomplete
                options={categoryOptions}
                size="small"
                value={toCategory ?? null}
                loading={categoryLoading}
                onChange={handleCategoryChange}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Select the Category"
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: (
                        <>
                          {categoryLoading ? <CircularProgress color="inherit" size={16} /> : null}
                          {params.InputProps.endAdornment}
                        </>
                      ),
                    }}
                  />
                )}
                isOptionEqualToValue={(option, value) => option.value === value.value}
              />

              <Typography variant="caption" color="text.secondary" className="-mt-2.5">
                {categoryLoading
                  ? "Loading clients…"
                  : toCategory
                  ? `Selected ${toCategory.label} (${to.length})`
                  : "Or search and pick recipients below"}
              </Typography>

              <Autocomplete
                multiple
                freeSolo
                open={toOpen}
                onOpen={() => setToOpen(true)}
                onClose={() => setToOpen(false)}
                options={[...filteredList]}
                value={to}
                size="small"
                loading={toOptionsLoading || categoryLoading}
                filterOptions={toCategory ? undefined : (x) => x}
                onInputChange={(_, value, reason) => {
                  if (toCategory) return;
                  if (reason === "reset") return;
                  setToInputValue(value);
                }}
                onChange={(_, val) => setTo(val)}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="To *"
                    helperText={
                      toCategory
                        ? undefined
                        : isWhatsApp
                        ? "Search clients who have a mobile number with a country code"
                        : "Type to search clients (paginated)"
                    }
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: (
                        <>
                          {toOptionsLoading || categoryLoading ? (
                            <CircularProgress color="inherit" size={16} />
                          ) : null}
                          {params.InputProps.endAdornment}
                        </>
                      ),
                    }}
                  />
                )}
                getOptionLabel={(option) =>
                  typeof option === "string" ? option : option?.name || ""
                }
                renderOption={(props, option) => (
                  <li {...props} key={option?.id}>
                    <div className="flex items-center justify-between gap-2 w-full">
                      <div className="flex items-center gap-2">
                        {isWhatsApp
                          ? option?.number
                            ? `${option?.name} (${option.number})`
                            : option?.name || ""
                          : option?.email
                          ? `${option?.name} (${option?.email})`
                          : option?.name || ""}
                      </div>
                    </div>
                  </li>
                )}
                isOptionEqualToValue={(option, value) => option.id === value.id}
                limitTags={3}
                disableCloseOnSelect={true}
              />

              {isWhatsApp && (
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={recordOptIn}
                      onChange={(event) => setRecordOptIn(event.target.checked)}
                    />
                  }
                  label="These clients have opted in to WhatsApp"
                />
              )}

              {!isWhatsApp && (
                <div>
                  <label className="block text-sm font-medium text-app-text mb-1">
                    External emails (optional)
                  </label>
                  <textarea
                    value={externalEmailsPaste}
                    onChange={(e) => setExternalEmailsPaste(e.target.value)}
                    placeholder="Paste from Excel — one per line, or comma / tab separated. Only valid emails are used."
                    rows={4}
                    className="w-full px-3 py-2 border border-app-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary text-sm resize-y min-h-[80px] bg-app-surface text-app-text"
                  />
                  {externalEmailsPaste.trim() &&
                    (() => {
                      const { valid, invalid } =
                        parseExternalEmailsWithInvalid(externalEmailsPaste);
                      return (
                        <div className="mt-1.5 space-y-1">
                          {valid.length > 0 && (
                            <p className="text-xs text-app-text-secondary">
                              <span className="font-medium text-green-600">
                                {valid.length} valid
                              </span>
                              {valid.length !== 1 ? " emails" : " email"} will be added as
                              recipients
                            </p>
                          )}
                          {invalid.length > 0 && (
                            <p className="text-xs text-red-600 flex items-start gap-1">
                              <span className="font-medium shrink-0">⚠</span>
                              <span>
                                <span className="font-medium">{invalid.length} invalid</span>{" "}
                                (ignored):
                                {invalid.slice(0, 5).join(", ")}
                                {invalid.length > 5 && ` +${invalid.length - 5} more`}
                              </span>
                            </p>
                          )}
                        </div>
                      );
                    })()}
                </div>
              )}
            </div>
          </Paper>

          {/* Message */}
          <Paper
            elevation={0}
            className="border border-app-border rounded-xl bg-app-surface shadow-sm overflow-hidden"
          >
            <SectionHeader icon={<DescriptionOutlinedIcon fontSize="small" />} title="Message" />
            <div className="p-4 flex flex-col gap-4">
              <div className="flex items-center gap-2">
                <Autocomplete
                  options={emailTemplates.map((tpl) => ({
                    templateName: tpl.templateName,
                    value: tpl.id,
                  }))}
                  value={emailTemplates.find((tpl) => tpl.id === template) || null}
                  size="small"
                  onChange={(_, val) => {
                    handleTemplateChange(val);
                  }}
                  renderInput={(params) => <TextField {...params} label="Template" />}
                  getOptionLabel={(option) => option.templateName}
                  isOptionEqualToValue={(option, value) => option.value === value.id}
                  className="flex-1"
                />

                {hasPermission(P.EMAIL.TEMPLATE_CREATE) && (
                  <Button
                    variant="outlined"
                    color="primary"
                    onClick={() => setTemplatesModalOpen(true)}
                  >
                    Manage Templates
                  </Button>
                )}
              </div>

              {isFestivalTemplate && (
                <TextField
                  label="Festival Name *"
                  value={festivalName}
                  onChange={handleFestivalNameChange}
                  size="small"
                  fullWidth
                  required
                  placeholder="e.g. Dashain, Christmas, Eid"
                  helperText="Required — replaces {festivalName} in subject and body"
                  error={!festivalName.trim()}
                />
              )}

              <div className="relative">
                <InputField
                  label={isWhatsApp ? "Subject" : "Subject"}
                  value={subject}
                  onChange={(e) => {
                    setCustom(true);
                    setSubject(e.target.value);
                  }}
                  inputRef={subjectRef}
                  required={!isWhatsApp}
                />
              </div>

              <Autocomplete
                multiple
                freeSolo
                options={["Admission", "Visa", "Accounts", "Follow Up", "Urgent"]}
                value={tags}
                size="small"
                onChange={(_, val) => setTags(val)}
                renderInput={(params) => (
                  <TextField {...params} label="Tags" placeholder="Add tag" />
                )}
                limitTags={4}
              />

              <div>
                <label className="block mb-1 font-medium text-sm text-app-text">Body</label>
                <ReactQuill
                  ref={quillRef}
                  className="kothar-compose-email"
                  value={body}
                  onChange={(val) => {
                    setCustom(true);
                    setBody(val);
                  }}
                  placeholder="Compose your message. Use the toolbar to add links or image URLs."
                  modules={quillModules}
                  formats={quillFormats}
                />
                {body.includes("{offerDetails}") && (
                  <p className="mt-1.5 text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded px-2 py-1.5">
                    ⚠ Replace <strong>{"{offerDetails}"}</strong> with actual offer details before
                    sending.
                  </p>
                )}
              </div>
            </div>
          </Paper>

          {/* Attachments */}
          <Paper
            elevation={0}
            className="border border-app-border rounded-xl bg-app-surface shadow-sm overflow-hidden"
          >
            <SectionHeader
              icon={<AttachFileOutlinedIcon fontSize="small" />}
              title="Attachments"
              trailing={
                attachments?.attachments?.length > 0 ? (
                  <Chip size="small" label={attachments.attachments.length} color="primary" />
                ) : null
              }
            />
            <div className="p-4">
              <UploadFile
                label="Attachment"
                data={attachments}
                setData={setAttachments}
                imageKey="attachments"
                onChange={(e) => setAttachments(Array.from(e.target.files))}
                type="multiple"
              />
            </div>
          </Paper>

          <Stack direction="row" alignItems="center" justifyContent="flex-end" gap={2}>
            <Button variant="outlined" onClick={() => navigate("/admin/emails")}>
              Cancel
            </Button>
            <Button
              variant="contained"
              color="primary"
              type="submit"
              disabled={isLoading}
              startIcon={
                isLoading ? (
                  <CircularProgress size={16} color="inherit" />
                ) : (
                  <SendOutlinedIcon fontSize="small" />
                )
              }
            >
              {isLoading ? "Sending…" : "Send"}
            </Button>
          </Stack>
        </form>

        {/* Preview */}
        <Paper
          elevation={0}
          className="xl:col-span-7 border border-app-border rounded-xl bg-app-surface shadow-sm overflow-hidden xl:sticky xl:top-[88px]"
        >
          <div className="px-4 py-3 border-b border-app-border flex items-center justify-between gap-3 bg-app-surface-muted/40">
            <div className="flex items-center gap-2">
              <VisibilityOutlinedIcon fontSize="small" className="text-app-text-muted" />
              <Typography
                variant="caption"
                className="font-bold uppercase tracking-wide text-app-text-muted"
              >
                Live Preview
              </Typography>
            </div>
            <Chip
              icon={<AttachFileOutlinedIcon />}
              label={
                attachments?.attachments?.length > 0
                  ? `${attachments.attachments.length} attachment(s)`
                  : "No attachments"
              }
              size="small"
              variant="outlined"
            />
          </div>

          <div className="px-4 py-3.5 border-b border-app-border">
            <div className="flex items-start gap-3">
              <div className="flex items-center justify-center w-9 h-9 rounded-full bg-primary/10 text-primary font-semibold text-sm shrink-0">
                {getActiveOrganizationName().charAt(0).toUpperCase()}
              </div>
              <div className="min-w-0 flex-1">
                <Typography variant="body2" className="font-semibold text-app-text break-words">
                  {subject || "(No subject)"}
                </Typography>
                <Typography variant="caption" className="text-app-text-muted block break-words">
                  {isWhatsApp ? "WhatsApp" : "From"}{" "}
                  <span className="text-app-text">
                    {isWhatsApp ? "individual chats" : from || "—"}
                  </span>{" "}
                  · To <span className="text-app-text">{toPreviewLabel}</span>
                </Typography>
                {tags.length > 0 && (
                  <Stack direction="row" spacing={0.5} flexWrap="wrap" mt={0.75}>
                    {tags.map((tag) => (
                      <Chip key={tag} label={tag} size="small" variant="outlined" />
                    ))}
                  </Stack>
                )}
              </div>
            </div>
            {attachments?.attachments?.length > 0 && (
              <Stack direction="row" spacing={1} flexWrap="wrap" mt={1.5}>
                {attachments.attachments.map((key, idx) => (
                  <Chip
                    key={`${key}-${idx}`}
                    label={getOriginalFileName(key) || `File ${idx + 1}`}
                    size="small"
                    variant="outlined"
                  />
                ))}
              </Stack>
            )}
          </div>

          {isWhatsApp ? (
            <Box className="p-6 bg-app-bg" sx={{ minHeight: 420 }}>
              <Box className="max-w-md ml-auto rounded-lg bg-[#dcf8c6] text-app-text px-3 py-2 whitespace-pre-wrap text-sm">
                {whatsappPreviewText}
              </Box>
              <Typography variant="caption" className="block text-app-text-muted mt-3">
                Files are sent as separate messages to each number. No WhatsApp group is created.
              </Typography>
            </Box>
          ) : (
            <Box
              className="bg-[#F3F6FA] overflow-auto"
              sx={{ minHeight: 900, maxHeight: "calc(100vh - 280px)" }}
            >
              <Box
                component="iframe"
                title="Email preview"
                srcDoc={emailPreviewHtml}
                sx={{
                  display: "block",
                  width: "100%",
                  minHeight: 900,
                  border: 0,
                  bgcolor: "#F3F6FA",
                }}
              />
            </Box>
          )}
        </Paper>
      </div>

      <EmailTemplatesModal
        open={templatesModalOpen}
        onClose={() => setTemplatesModalOpen(false)}
        onTemplateSelect={handleTemplateSelectFromModal}
        refetchTemplates={refetchEmailTemplates}
      />
    </div>
  );
};

export default ComposeEmailPage;
